import React, { type ComponentProps } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { Modal, Pressable, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';

import { BodyText, Button, theme, useResponsive } from '@sg/garnish';

import { BUNDLE_DIALOG_WIDTH } from '../../Bundle.constants';
import { type BundleSelectProduct } from '../../Bundle.types';
import { BundleSelectList } from '../BundleSelectList';

// ─────────────────────────────────────────────────────────────────────────────

export const BundleSelectModal = (props: Props) => {
  const { isVisible, title, products, selectedProductSlug, onSelect, onClose, onContinue } = props;

  const { formatMessage } = useIntl();
  const { currentBreakpoint } = useResponsive();
  const { bottom: safeAreaBottomInset } = useSafeAreaInsets();

  // ─── Derived ─────────────────────────────────────────────────────────

  const canContinue = selectedProductSlug !== undefined;

  const isDialog = !currentBreakpoint.isXS;

  // ─── Styles ──────────────────────────────────────────────────────────

  const sheetDynamicStyles = useStyle(
    () =>
      isDialog
        ? undefined
        : { marginBottom: Math.max(SHEET_MIN_BOTTOM_SPACING, safeAreaBottomInset) },
    [isDialog, safeAreaBottomInset],
  );

  const containerStyles = [
    styles.container,
    isDialog ? styles.containerDialog : styles.containerSheet,
  ];

  const sheetStyles = [
    styles.sheet,
    isDialog ? styles.sheetDialog : styles.sheetBottom,
    sheetDynamicStyles,
  ];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Modal transparent animationType="fade" visible={isVisible} onRequestClose={onClose}>
      <View style={containerStyles}>
        <Pressable
          accessibilityLabel={formatMessage(messages.dismiss)}
          accessibilityRole="button"
          style={styles.scrim}
          onPress={onClose}
        />

        <View style={sheetStyles}>
          <BodyText sizeMatch={TITLE_SIZE_MATCH} style={styles.title} bold>
            {title}
          </BodyText>

          <BundleSelectList
            products={products}
            selectedProductSlug={selectedProductSlug}
            onSelect={onSelect}
          />

          <Button
            size="large-wide"
            accessibilityLabel={formatMessage(messages.continue)}
            disabled={!canContinue}
            onPress={onContinue}
          >
            <FormattedMessage {...messages.continue} />
          </Button>
        </View>
      </View>
    </Modal>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const SHEET_MIN_BOTTOM_SPACING = theme.spacing['6'];
const SHEET_SIDE_SPACING = theme.spacing['4'];
const SHEET_MAX_HEIGHT = '80%';
const DIALOG_WIDTH = BUNDLE_DIALOG_WIDTH - SHEET_SIDE_SPACING * 2;
const DIALOG_MAX_HEIGHT = 720;

const TITLE_SIZE_MATCH: ComponentProps<typeof BodyText>['sizeMatch'] = ['16'];

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.DARK,
  },
  containerSheet: {
    justifyContent: 'flex-end',
  },
  containerDialog: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing['10'],
  },
  scrim: {
    ...StyleSheet.absoluteFill,
  },
  sheet: {
    flexShrink: 1,
    gap: theme.spacing['4'],
    padding: theme.spacing['4'],
    paddingTop: theme.spacing['6'],
    borderRadius: theme.radius.xlarge,
    backgroundColor: theme.colors.WHITE,
  },
  sheetBottom: {
    maxHeight: SHEET_MAX_HEIGHT,
    marginHorizontal: SHEET_SIDE_SPACING,
  },
  sheetDialog: {
    width: DIALOG_WIDTH,
    maxWidth: '100%',
    maxHeight: DIALOG_MAX_HEIGHT,
  },
  title: {
    color: theme.colors.DARK_KALE,
    textAlign: 'center',
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  continue: {
    defaultMessage: 'Continue',
    description: 'Bundle | Select modal | Continue CTA',
  },
  dismiss: {
    defaultMessage: 'Dismiss',
    description: 'Bundle | Select modal | Scrim dismiss accessibility label',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  isVisible: boolean;
  title: string;
  products: BundleSelectProduct[];
  selectedProductSlug?: string;
  onSelect: (product: BundleSelectProduct) => void;
  onClose: () => void;
  onContinue: () => void;
};
